import React from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSave } from "@/hooks/useSave";
import { useListings } from "@/hooks/useListings";
import { HERO_IMAGE, CATEGORIES, TRAVEL_FEATURE_IMAGE } from "@/data/catalog";
import { Plane, ShoppingBag, Flower2, Compass, ArrowRight, Sparkles, Bookmark } from "lucide-react";

const ICONS = { Plane, ShoppingBag, Flower2, Compass };

const Home = () => {
  const { setPlannerOpen } = useApp();
  const { save } = useSave();
  const navigate = useNavigate();
  const { data: destinations = [] } = useListings("destination");

  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Luxury escape" className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070709] via-[#070709]/85 to-[#070709]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-xs uppercase tracking-[0.35em] text-amber-400/90 font-semibold mb-6">
              Plan · Shop · Travel · Self-Care · Live
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] text-white">
              Your complete <span className="gold-gradient-text italic">lifestyle</span> concierge.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              MOVA helps you plan your trips, shop your favourite brands, book self-care, and manage your lifestyle goals — all in one place.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setPlannerOpen(true)}
                data-testid="plan-my-lifestyle-btn"
                className="rounded-full h-14 px-8 text-base bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium hover:opacity-90 gold-glow"
              >
                <Sparkles size={18} className="mr-2" /> Plan My Lifestyle
              </Button>
              <Button
                onClick={() => navigate("/travel")}
                variant="outline"
                data-testid="hero-explore-btn"
                className="rounded-full h-14 px-8 text-base border-amber-400/30 text-white hover:bg-white/5 bg-transparent"
              >
                Explore Concierge Services <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Category bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="categories-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.icon];
            const span = i === 0 ? "md:col-span-7" : i === 3 ? "md:col-span-12" : "md:col-span-5";
            return (
              <Link
                to={c.href}
                key={c.id}
                data-testid={`category-card-${c.id}`}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-amber-400/50 transition-colors duration-500 min-h-[260px] ${span}`}
              >
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[#08080c]/60 to-transparent" />
                <div className="relative p-8 h-full flex flex-col justify-end">
                  <div className="w-11 h-11 rounded-full glass gold-border flex items-center justify-center text-champagne mb-4">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-serif text-2xl text-white">{c.title}</h3>
                  <p className="text-slate-300 mt-2 text-sm max-w-md">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm text-champagne group-hover:gap-3 transition-all duration-200">
                    {c.cta} <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Luxury trips feature */}
      <section className="relative py-24 border-y border-white/5" data-testid="luxury-trips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative rounded-3xl overflow-hidden min-h-[420px] gold-border">
            <img src={TRAVEL_FEATURE_IMAGE} alt="Luxury travel" className="w-full h-full object-cover absolute inset-0" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-semibold mb-4">Travel</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-white leading-tight">Luxury trips, curated for you.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-lg">
              From weekend getaways to dream destinations, MOVA makes travel effortless.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              {["Flights", "Hotels", "Experiences", "Group Trips"].map((f) => (
                <div key={f} className="flex items-center gap-3 text-slate-200">
                  <span className="text-champagne">✦</span> {f}
                </div>
              ))}
            </div>
            <Button onClick={() => navigate("/travel")} data-testid="explore-trips-btn" className="mt-10 rounded-full h-12 px-7 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium hover:opacity-90">
              Explore Trips <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured destinations */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="destinations-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="font-serif text-3xl sm:text-5xl text-white">Featured Destinations</h2>
            <p className="text-slate-400 mt-3">Handpicked destinations for your next escape.</p>
          </div>
          <Link to="/travel" className="hidden sm:inline-flex items-center gap-2 text-champagne hover:gap-3 transition-all duration-200" data-testid="view-all-destinations">
            View All <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {destinations.map((d) => (
            <div key={d.id} data-testid={`destination-card-${d.id}`} className="group relative rounded-3xl overflow-hidden border border-amber-500/15 min-h-[380px] hover:border-amber-400/40 transition-colors duration-500">
              <img src={d.image} alt={d.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-[#070709]/30 to-transparent" />
              <button
                onClick={() => save({ item_type: "destination", title: d.title, subtitle: d.subtitle, image: d.image, meta: { price: d.price } })}
                data-testid={`save-destination-${d.id}`}
                className="absolute top-4 right-4 w-10 h-10 rounded-full glass gold-border flex items-center justify-center text-champagne hover:bg-amber-400/20 transition-colors duration-200"
              >
                <Bookmark size={16} />
              </button>
              <div className="relative h-full flex flex-col justify-end p-6">
                <span className="text-xs text-amber-300/90 mb-2">{d.price}</span>
                <h3 className="font-serif text-2xl text-white">{d.title}</h3>
                <p className="text-slate-300 text-sm mt-1">{d.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative rounded-3xl overflow-hidden gold-border grain p-10 sm:p-16 bg-gradient-to-br from-[#16160f] to-[#0b0b10] text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-champagne">Let MOVA curate your next move.</h2>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Tell us the vibe — we craft the plan, book the details and keep it all in one place.</p>
          <Button onClick={() => setPlannerOpen(true)} data-testid="cta-plan-btn" className="mt-8 rounded-full h-13 px-8 py-6 bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] font-medium hover:opacity-90">
            <Sparkles size={18} className="mr-2" /> Plan My Lifestyle
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
